// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.

import { createSelector } from 'reselect'

import { character, place } from '../store/initialState'
import { fullFileStateSelector } from './fullFileFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('../index').LegacyCustomAttributes} LegacyCustomAttributes
 */

const characterKeys = Object.keys(character)
const placeKeys = Object.keys(place)

/**
 * @type {function(PlottrState): LegacyCustomAttributes}
 */
export const allCustomAttributesSelector = createSelector(
  fullFileStateSelector,
  ({ customAttributes }) => customAttributes
)
/**
 * @type {function(PlottrState): Array<LegacyCustomAttribute>}
 */
export const characterCustomAttributesSelector = createSelector(
  allCustomAttributesSelector,
  ({ characters }) => characters
)
/**
 * @type {function(PlottrState): Array<LegacyCustomAttribute>}
 */
export const placeCustomAttributesSelector = createSelector(
  allCustomAttributesSelector,
  ({ places }) => places
)
/**
 * @type {function(PlottrState): Array<LegacyCustomAttribute>}
 */
export const cardsCustomAttributesSelector = createSelector(
  allCustomAttributesSelector,
  ({ scenes }) => scenes
)
/**
 * @type {function(PlottrState): Array<LegacyCustomAttribute>}
 */
export const noteCustomAttributesSelector = createSelector(
  allCustomAttributesSelector,
  ({ notes }) => notes
)

/**
 * Produce all the names of the legacy custom attributes
 * for characters.  The name implies that we'll likely use
 * them in the sort popup on the character tab.
 *
 * @type {function(PlottrState): Array<String>}
 */
export const characterSortCAnamesSelector = createSelector(
  characterCustomAttributesSelector,
  (attributes) => attributes.filter((attr) => attr.type == 'text').map((attr) => attr.name)
)

/**
 * @type {function(PlottrState): Array<String>}
 */
export const placeSortCAnamesSelector = createSelector(
  placeCustomAttributesSelector,
  (attributes) => attributes.filter((attr) => attr.type == 'text').map((attr) => attr.name)
)

/**
 * @type {function(PlottrState): Array<String>}
 */
export const noteSortCAnamesSelector = createSelector(noteCustomAttributesSelector, (attributes) =>
  attributes.filter((attr) => attr.type == 'text').map((attr) => attr.name)
)

/**
 * @type {function(PlottrState): Array<String>}
 */
export const characterCustomAttributesRestrictedValues = createSelector(
  characterCustomAttributesSelector,
  (attrs) => characterKeys.concat(attrs.map((a) => a.name))
)

/**
 * @type {function(PlottrState): Array<String>}
 */
export const placeCustomAttributesRestrictedValues = createSelector(
  placeCustomAttributesSelector,
  (attrs) => placeKeys.concat(attrs.map((a) => a.name))
)

/**
 * @type {function(PlottrState): Array<String>}
 */
export const noteCustomAttributesRestrictedValues = createSelector(
  noteCustomAttributesSelector,
  (attrs) => placeKeys.concat(attrs.map((a) => a.name))
)

/**
 * @param {PlottrState} _state
 * @param {String} attributeName
 * @return {String}
 */
const attributeNameSelector = (_state, attributeName) => {
  return attributeName
}

/**
 * @type {function(PlottrState, String): (LegacyCustomAttribute | Null)}
 */
export const legacyCustomCharacterAttributeByName = createSelector(
  characterCustomAttributesSelector,
  attributeNameSelector,
  (customAttributes, name) => {
    return (
      customAttributes.find((attribute) => {
        return attribute.name === name
      }) ?? null
    )
  }
)
